import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { DemoBadge, EmptyState } from "@/components/shared";
import { Search } from "lucide-react";
import { toast } from "sonner";

export default function Keywords() {
  const { activeWebsite } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    if (!activeWebsite) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      const { data: d } = await api.get(`/websites/${activeWebsite.id}/keywords`, { params });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [activeWebsite?.id, page]); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Keywords</h1>
          <p className="text-xs text-muted-foreground">Queries aggregated over the last 28 days from Search Console data.</p>
        </div>
        <Input placeholder="Filter queries…" value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} className="h-8 w-56 text-xs" data-testid="keywords-search-input" />
      </div>

      {loading ? <Skeleton className="h-64 w-full" /> : !data || data.total === 0 ? (
        <EmptyState icon={Search} title="No query data yet" message="Connect Google Search Console (or load labelled demo data) to see queries here." testId="keywords-empty" />
      ) : (
        <Card className="shadow-none">
          <CardContent className="scc-table-wrap p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                  <TableHead className="text-right">Impressions</TableHead>
                  <TableHead className="text-right">CTR</TableHead>
                  <TableHead className="text-right">Avg position</TableHead>
                  <TableHead className="hidden lg:table-cell">Top page</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((k) => (
                  <TableRow key={k.query} data-testid="keywords-table-row">
                    <TableCell className="max-w-[240px] truncate text-xs font-medium">
                      {k.query} {k.is_demo && <DemoBadge />}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{k.clicks.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{k.impressions.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{(k.ctr * 100).toFixed(1)}%</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{k.position}</TableCell>
                    <TableCell className="hidden max-w-[240px] truncate font-mono text-[10px] text-muted-foreground lg:table-cell">{k.pages?.[0]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {data && data.total > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page * 50 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
